import { useEffect, useRef } from 'react';
import type { CallDetails, FactPopoverData, PortalDetails } from './types';
import { SOURCE_LABEL } from './types';

function useDismiss(active: boolean, onClose: () => void, panel: React.RefObject<HTMLElement | null>) {
  useEffect(() => {
    if (!active) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    panel.current?.focus();
    return () => { document.removeEventListener('keydown', onKey); };
  }, [active, onClose, panel]);
}

const WHO_LABEL: Record<CallDetails['transcript'][number]['who'], string> = {
  menu: 'Phone menu', elva: 'ELVA', insurer: 'Insurer', hold: 'Hold',
};

/** AI Call drawer (handoff §4.12). No "Ask the insurer something else" button. */
export function CallDrawer({ call, onClose }: { call: CallDetails; onClose: () => void }) {
  const panel = useRef<HTMLDivElement>(null);
  useDismiss(true, onClose, panel);
  return (
    <div className="ins-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="ins-drawer" role="dialog" aria-modal="true" aria-label="Insurance call details" tabIndex={-1} ref={panel}>
        <div className="ins-drawer-head">
          <div>
            <div className="ins-drawer-eyebrow">{call.title}</div>
            <h3 className="ins-drawer-title">{call.subtitle}</h3>
          </div>
          <button type="button" className="ins-drawer-close" aria-label="Close" onClick={onClose}>×</button>
        </div>
        <div className="ins-drawer-body">
          <dl className="ins-dl">
            <dt>Number called</dt><dd>{call.numberCalled}</dd>
            <dt>When</dt><dd>{call.when}</dd>
            <dt>Length</dt><dd>{call.length}</dd>
            <dt>Why ELVA called</dt><dd>{call.why}</dd>
            <dt>Spoke with</dt><dd>{call.rep}</dd>
            <dt>Call reference</dt><dd>{call.reference}</dd>
          </dl>
          <div className="ins-drawer-sec">
            <h4>What the call confirmed</h4>
            <ul className="ins-confirm">
              {call.confirmed.map((c, i) => (
                <li key={i}><span>{c.label}</span><span className="v">{c.value}</span></li>
              ))}
            </ul>
          </div>
          <div className="ins-drawer-sec">
            <h4>Recording</h4>
            <div className="ins-recording">
              <span className="play" aria-hidden>▶</span>
              <span className="track" aria-hidden />
              <span>0:00 / {call.recordingLength}</span>
            </div>
          </div>
          <div className="ins-drawer-sec">
            <h4>Transcript</h4>
            <div className="ins-tx">
              {call.transcript.map((t, i) => (
                <div className={`ins-tx-row ${t.who}`} key={i}>
                  <div className="who">{WHO_LABEL[t.who]}</div>
                  <div className="text">{t.text}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="ins-drawer-foot">Recording and transcript kept with the patient’s record.</div>
      </div>
    </div>
  );
}

/** Payer Portal drawer (handoff §4.12). No stopped-login block. */
export function PortalDrawer({ portal, onClose }: { portal: PortalDetails; onClose: () => void }) {
  const panel = useRef<HTMLDivElement>(null);
  useDismiss(true, onClose, panel);
  return (
    <div className="ins-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="ins-drawer" role="dialog" aria-modal="true" aria-label="Portal download details" tabIndex={-1} ref={panel}>
        <div className="ins-drawer-head">
          <div>
            <div className="ins-drawer-eyebrow">{portal.title}</div>
            <h3 className="ins-drawer-title">{portal.subtitle}</h3>
          </div>
          <button type="button" className="ins-drawer-close" aria-label="Close" onClick={onClose}>×</button>
        </div>
        <div className="ins-drawer-body">
          <dl className="ins-dl">
            <dt>Portal</dt><dd>{portal.portal}</dd>
            <dt>When</dt><dd>{portal.when}</dd>
            <dt>Why</dt><dd>{portal.why}</dd>
            <dt>Login used</dt><dd>{portal.login}</dd>
          </dl>
          <div className="ins-drawer-sec">
            <h4>Document</h4>
            <div className="ins-thumbs">
              {Array.from({ length: portal.pages }, (_, i) => (
                <span className="ins-thumb" key={i}>p.{i + 1}</span>
              ))}
            </div>
            <p className="ins-note">Benefit summary, {portal.pages} pages · saved to Document Center · <button type="button" className="ins-link">Open</button></p>
          </div>
          <div className="ins-drawer-sec">
            <h4>{portal.facts.length} facts read from it</h4>
            {portal.facts.map((f, i) => (
              <div className="ins-portal-fact" key={i}>
                <span>
                  {f.text}
                  {f.newInfo ? <span className="ins-new"> · new, not in eligibility check</span> : null}
                </span>
                <span className="pg">{f.page}</span>
              </div>
            ))}
            <button type="button" className="ins-link" style={{ marginTop: 8 }}>Open document</button>
          </div>
        </div>
      </div>
    </div>
  );
}

const TAG_LABEL = { agrees: 'Agrees', no_answer: 'No answer', na: '—' } as const;

/** Fact popover (handoff §4.12). From any source marker. */
export function FactPopover({ fact, onClose, onWrong }: { fact: FactPopoverData; onClose: () => void; onWrong: () => void }) {
  const panel = useRef<HTMLDivElement>(null);
  useDismiss(true, onClose, panel);
  return (
    <div className="ins-pop-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="ins-pop" role="dialog" aria-modal="true" aria-label={fact.title} tabIndex={-1} ref={panel}>
        <div className="ins-pop-head">
          <div>
            <div className="ins-drawer-eyebrow">Where this came from</div>
            <h3 className="ins-drawer-title" style={{ fontSize: 16 }}>{fact.title}</h3>
          </div>
          <button type="button" className="ins-drawer-close" aria-label="Close" onClick={onClose}>×</button>
        </div>
        <div className="ins-pop-body">
          <p className="ins-note" style={{ marginTop: 0 }}>{fact.uses} {fact.summary}</p>
          <div style={{ marginTop: 6 }}>
            {fact.sources.filter((s) => s.tag !== 'no_answer').map((s) => (
              <div className="ins-pop-src" key={s.kind}>
                <div className="head">
                  <strong>{SOURCE_LABEL[s.kind]}</strong>
                  {s.weak ? <span className="ins-vtag na">verbal</span> : null}
                  <span className={`ins-vtag ${s.tag}`}>{TAG_LABEL[s.tag]}</span>
                </div>
                <div className="ans">{s.answer}{s.link ? <> · <button type="button" className="ins-link">{s.link}</button></> : null}</div>
                {(s.citation || s.at) ? (
                  <div className="ins-cite">{[s.at, s.citation].filter(Boolean).join(' · ')}{s.weak ? ' · verbal, not independently documented' : ''}</div>
                ) : null}
              </div>
            ))}
            {(() => {
              const none = fact.sources.filter((s) => s.tag === 'no_answer');
              return none.length ? (
                <div className="ins-pop-src"><div className="ans">No answer from: {none.map((s) => SOURCE_LABEL[s.kind]).join(', ')}</div></div>
              ) : null;
            })()}
          </div>
          {fact.usedIn ? (
            <div className="ins-drawer-sec">
              <h4>Used in</h4>
              <p className="ins-note" style={{ marginTop: 0 }}>{fact.usedIn}</p>
            </div>
          ) : null}
          <button type="button" className="ins-btn ins-btn-secondary" style={{ marginTop: 10 }} onClick={onWrong}>This is wrong</button>
        </div>
      </div>
    </div>
  );
}
